import {
  ChevronLeftIcon,
  ChevronRightIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { classNames, isCurrent } from "@via/utils/helpers.tsx";
import { type Tab } from "@via/utils/interfaces.tsx";
import { type JSX, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const SCROLL_AMOUNT = 150;

type TabVariant = "underline" | "button-group";

interface HorizontalTabsProps<T extends string> {
  hidden?: T[];
  buttonIdPrefix?: string;
  options: Tab<T>[];
  defaultActiveTab?: T;
  activeTab?: T; // Optional controlled prop
  onTabChange?: (tabName: T) => void; // Optional controlled prop
  variant?: TabVariant;
  fullWidth?: boolean;
}

export const HorizontalTabs = <T extends string>({
  hidden = [],
  options,
  buttonIdPrefix,
  defaultActiveTab,
  activeTab: controlledActiveTab, // Controlled active tab (optional)
  onTabChange: controlledOnTabChange, // Handler to change the active tab (optional)
  variant = "underline",
  fullWidth = false,
}: HorizontalTabsProps<T>): JSX.Element | null => {
  const { pathname } = useLocation();
  const visibleOptions = options.filter(
    ({ name }) => name && !hidden.includes(name)
  );

  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<T>(
    defaultActiveTab || options[0]?.name || ("" as T)
  );

  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : uncontrolledActiveTab;

  const navRef = useRef<HTMLElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = navRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, visibleOptions.length]);

  useEffect(() => {
    if (defaultActiveTab && !isControlled) {
      setUncontrolledActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab, isControlled]);

  if (options.length === 0) return null;

  const handleClick = (tabName: T) => {
    if (isControlled) {
      controlledOnTabChange?.(tabName);
    } else {
      setUncontrolledActiveTab(tabName);
    }
  };

  const scroll = (direction: "left" | "right") => {
    const el = navRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };
  const tabContent = (
    <>
      {options
        .filter(({ children }) => children)
        .map(({ name, children }) => (
          <div
            key={name}
            className={classNames(activeTab === name ? "block" : "hidden")}>
            {children}
          </div>
        ))}
    </>
  );

  if (variant === "button-group") {
    return (
      <div className="w-full sm:block">
        <div className="relative">
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scroll("left")}
              className="absolute left-0 top-0 z-10 flex h-full items-center bg-gradient-to-r from-white via-white/80 to-transparent pl-1 pr-2"
              aria-label="Scroll tabs left">
              <ChevronLeftIcon className="h-4 w-4 text-gray-500" />
            </button>
          )}
          <nav
            ref={navRef}
            className={classNames(
              "isolate overflow-x-auto rounded-md shadow-sm scrollbar-hidden",
              fullWidth ? "flex w-full" : "inline-flex"
            )}
            aria-label="Tabs">
            {visibleOptions.map((tab, idx) => {
              const tabButtonId = `tab-${buttonIdPrefix ?? "no-prefix"}-${tab.name.toLowerCase().split(" ").join("-")}`;
              const isActive = tab.href
                ? isCurrent(tab.name, pathname)
                : activeTab === tab.name;
              const isFirst = idx === 0;
              const isLast = idx === visibleOptions.length - 1;

              const className = classNames(
                "relative inline-flex h-9 items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10",
                isFirst ? "rounded-l-md" : "-ml-px",
                isLast ? "rounded-r-md" : "",
                fullWidth ? "flex-1 justify-center" : "",
                isActive
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 hover:shadow-md"
                  : "bg-white text-gray-900 hover:bg-gray-50"
              );

              return tab.href ? (
                <Link
                  id={tabButtonId}
                  key={tab.name}
                  to={tab.href}
                  className={className}
                  aria-current={isActive ? "page" : undefined}>
                  {tab.customTitle || tab.name}
                </Link>
              ) : (
                <button
                  id={tabButtonId}
                  key={tab.name}
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    handleClick(tab.name);
                  }}
                  className={className}
                  aria-current={isActive ? "page" : undefined}>
                  {tab.customTitle || tab.name}
                </button>
              );
            })}
          </nav>
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scroll("right")}
              className="absolute right-0 top-0 z-10 flex h-full items-center bg-gradient-to-l from-white via-white/80 to-transparent pl-2 pr-1"
              aria-label="Scroll tabs right">
              <ChevronRightIcon className="h-4 w-4 text-gray-500" />
            </button>
          )}
        </div>
        {tabContent}
      </div>
    );
  }

  return (
    <div className="w-full sm:block">
      <div className="relative border-b border-gray-200">
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute left-0 top-0 z-10 flex h-full items-center bg-gradient-to-r from-white via-white/80 to-transparent pl-1 pr-2"
            aria-label="Scroll tabs left">
            <ChevronLeftIcon className="h-4 w-4 text-gray-500" />
          </button>
        )}
        <nav
          ref={navRef}
          className={classNames(
            "-mb-px flex overflow-x-auto scrollbar-hidden",
            fullWidth ? "w-full" : "space-x-8"
          )}
          aria-label="Tabs">
          {visibleOptions.map(tab => {
            const tabButtonId = `tab-${buttonIdPrefix ?? "no-prefix"}-${tab.name.toLowerCase().split(" ").join("-")}`;
            const isActive = tab.href
              ? isCurrent(tab.name, pathname)
              : activeTab === tab.name;

            const tabClassName = classNames(
              isActive
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700",
              "flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium duration-300 ease-in-out",
              fullWidth ? "flex-1 justify-center" : ""
            );

            return tab.href ? (
              <Link
                id={tabButtonId}
                key={tab.name}
                to={tab.href}
                className={tabClassName}
                aria-current={isActive ? "page" : undefined}>
                {tab.customTitle || tab.name}
              </Link>
            ) : (
              <button
                id={tabButtonId}
                key={tab.name}
                onClick={e => {
                  e.preventDefault();
                  handleClick(tab.name);
                }}
                className={tabClassName}>
                {tab.customTitle || tab.name}
              </button>
            );
          })}
        </nav>
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute right-0 top-0 z-10 flex h-full items-center bg-gradient-to-l from-white via-white/80 to-transparent pl-2 pr-1"
            aria-label="Scroll tabs right">
            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
          </button>
        )}
      </div>
      {tabContent}
    </div>
  );
};

export const VerticalTabs = <T extends string>({
  hidden = [],
  title,
  options,
  defaultActiveTab,
  activeTab: controlledActiveTab, // Controlled active tab (optional)
  onTabChange: controlledOnTabChange, // Handler to change the active tab (optional)
  onDelete, // Handler to delete the tab (optional)
}: {
  hidden?: T[];
  title: T;
  options: Tab<T>[];
  defaultActiveTab?: T;
  activeTab?: T; // Optional controlled prop
  onTabChange?: (tabName: T) => void; // Optional controlled prop
  onDelete?: (id: number, name: string) => void; // Optional controlled prop
}): JSX.Element | null => {
  const visibleOptions = options.filter(
    ({ name }) => name && !hidden.includes(name)
  );

  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<T>(
    defaultActiveTab || ("" as T)
  );
  const [showDeleteIcon] = useState(onDelete ? true : false);

  const isControlled = controlledActiveTab !== undefined;

  const activeTab = isControlled ? controlledActiveTab : uncontrolledActiveTab;

  useEffect(() => {
    if (defaultActiveTab && !isControlled) {
      setUncontrolledActiveTab(defaultActiveTab);
    }
  }, [defaultActiveTab, isControlled]);

  if (options.length === 0) return null;

  const handleClick = (tabName: T, tabOnClick: (() => void) | undefined) => {
    if (isControlled) {
      controlledOnTabChange?.(tabName);
    } else {
      setUncontrolledActiveTab(tabName);
    }
    tabOnClick && tabOnClick();
  };

  return (
    <div className="w-52 min-w-full">
      <ul role="list">
        <li className="text-sm font-bold text-gray-900">{title}</li>
        {visibleOptions.map(item => (
          <li key={item.name} className="flex justify-between">
            <button
              onClick={e => {
                e.preventDefault();
                handleClick(item.name, item.onClick || undefined);
              }}
              className={classNames(
                "text-gray-700 hover:text-indigo-600",
                "group flex gap-x-3 rounded-md py-2 text-sm font-semibold leading-6"
              )}>
              {item.href ? (
                <Link to={item.href}>{item.customTitle || item.name}</Link>
              ) : (
                item.customTitle || item.name
              )}
            </button>
            <div className="flex items-center">
              {item.trailing}
              {showDeleteIcon && (
                <button
                  className="mr-4"
                  onClick={() =>
                    onDelete && onDelete(item.id as number, item.name)
                  }>
                  <TrashIcon className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <div>
        {options
          .filter(({ children }) => children)
          .map(({ name, children }) => (
            <div
              key={name}
              className={classNames(activeTab === name ? "block" : "hidden")}>
              {children}
            </div>
          ))}
      </div>
    </div>
  );
};
